import { useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPencilAlt } from "@fortawesome/free-solid-svg-icons";
import useAnchoredToRect from "../hooks/useAnchoredToRect";
import { useAnnotations } from "../contexts/AnnotationsContext";
import { HighlightColorDot } from "./HighlightColorSelector";
import { HIGHLIGHT_COLORS } from "../utils/annotator/highlightColors";
import "./NoteButton.css";

/**
 * Floating action card anchored to a text selection. Offers Highlight and Note
 * actions for a new selection:
 *  - Highlight - just highlight the range in the dom with no note text
 *  - Note - Open the NoteModal and allow the user to enter note text
 *
 * Returns `null` when there is no active selection to anchor to.
 *
 * @param {Object} props
 * @param {(colorValue: string) => void} [props.onHighlightColorSelected] - Invoked with the chosen color when a highlight dot is clicked.
 * @param {() => void} [props.onCreateNote] - Invoked when the Note action is clicked.
 * @returns {JSX.Element | null}
 */
function NoteButton({ onHighlightColorSelected, onCreateNote }) {
  const { selectNoteButton } = useAnnotations();
  const buttonRef = useRef(null);

  const { getViewportRect } = selectNoteButton ?? {};

  const { style } = useAnchoredToRect(buttonRef, getViewportRect);

  if (!selectNoteButton) return null;

  return (
    <div ref={buttonRef} style={style}>
      <div className="noteButtonCard bg-white shadow-lg d-flex">
        <div role="group" aria-label="Highlight" className="d-flex">
          {HIGHLIGHT_COLORS.map((highlightColor) => (
            <button
              key={highlightColor.value}
              type="button"
              className="noteButtonCard__action noteButtonCard__colorAction"
              aria-label={`Highlight in ${highlightColor.label}`}
              onClick={() => onHighlightColorSelected(highlightColor.value)}
            >
              <HighlightColorDot colorValue={highlightColor.value} />
            </button>
          ))}
        </div>
        <div className="noteButtonCard__divider" />
        <button
          type="button"
          className="noteButtonCard__action"
          onClick={onCreateNote}
        >
          <FontAwesomeIcon
            icon={faPencilAlt}
            className="mr-2"
            role="presentation"
          />
          Note
        </button>
      </div>
    </div>
  );
}

export default NoteButton;
